import { create } from "zustand";
import { ROLE_VALUES, type Role } from "@/components/users/roles";
import { getJwtToken } from "@/lib/auth-client";

/**
 * Role precedence — higher rank = stronger.
 * admin > manager > inspector > viewer
 */
export const ROLE_RANK: Record<Role, number> = ROLE_VALUES.reduce(
  (acc, role, idx) => {
    acc[role] = ROLE_VALUES.length - 1 - idx;
    return acc;
  },
  {} as Record<Role, number>,
);

export interface AuthUser {
  id: string;
  email: string;
  name?: string | null;
  image?: string | null;
}

interface AuthState {
  user: AuthUser | null;
  /** Single platform role from session.user.role. */
  role: Role | null;
  activeOrganizationId: string | null;
  /** Site IDs the user has access to (from JWT siteClaims). */
  siteIds: string[];
  /** Set form of siteIds for O(1) membership checks during render. */
  siteIdSet: Set<string>;
  /** Mirrors better-auth's session loading state. */
  isPending: boolean;

  // Actions
  setSession: (input: {
    user: AuthUser | null;
    role: Role | null;
    activeOrganizationId: string | null;
    siteIds: string[];
  }) => void;
  setSiteIds: (siteIds: string[]) => void;
  setIsPending: (isPending: boolean) => void;
  clear: () => void;

  // Selectors
  /** Current bearer token (read-through to in-memory JWT). */
  getToken: () => string | null;
  isAuthenticated: () => boolean;
  isPlatformAdmin: () => boolean;
  /**
   * True if the user's role meets or exceeds `minRole`. When `siteId` is
   * passed, the user must also have access to that site (admins bypass).
   */
  canAccess: (minRole: Role, siteId?: string) => boolean;
  /**
   * True if the user can view the given site. With no `siteId` it just
   * means "signed in". Platform admins always pass.
   */
  canView: (siteId?: string) => boolean;
}

const initial = {
  user: null,
  role: null,
  activeOrganizationId: null,
  siteIds: [] as string[],
  siteIdSet: new Set<string>(),
  isPending: true,
};

function siteIdsEqual(a: string[], b: string[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  ...initial,

  setSession: ({ user, role, activeOrganizationId, siteIds }) => {
    const prev = get();
    // Skip no-op updates so consumers don't re-render unnecessarily.
    const samePrimitives =
      prev.user?.id === user?.id &&
      prev.user?.email === user?.email &&
      prev.user?.name === user?.name &&
      prev.user?.image === user?.image &&
      prev.role === role &&
      prev.activeOrganizationId === activeOrganizationId &&
      siteIdsEqual(prev.siteIds, siteIds);
    if (samePrimitives) return;
    set({
      user,
      role,
      activeOrganizationId,
      siteIds,
      siteIdSet: new Set(siteIds),
    });
  },

  setSiteIds: (siteIds) => {
    if (siteIdsEqual(get().siteIds, siteIds)) return;
    set({ siteIds, siteIdSet: new Set(siteIds) });
  },

  setIsPending: (isPending) => {
    if (get().isPending === isPending) return;
    set({ isPending });
  },

  clear: () => set({ ...initial, siteIdSet: new Set<string>() }),

  getToken: () => getJwtToken(),

  isAuthenticated: () => get().user !== null,

  isPlatformAdmin: () => get().role === "admin",

  canAccess: (minRole, siteId) => {
    const { user, role, siteIdSet } = get();
    if (!user || !role) return false;
    if (role === "admin") return true;
    if (siteId && !siteIdSet.has(siteId)) return false;
    return ROLE_RANK[role] >= ROLE_RANK[minRole];
  },

  canView: (siteId) => {
    const { user, role, siteIdSet } = get();
    if (!user) return false;
    if (role === "admin") return true;
    if (!siteId) return true;
    return siteIdSet.has(siteId);
  },
}));
